import React, { useState, useEffect, useMemo } from 'react';
import { 
  DocumentTextIcon, 
  VideoCameraIcon, 
  PhoneIcon, 
  ChatBubbleLeftRightIcon, 
  EnvelopeIcon, 
  GlobeAltIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import SectionTitle from './text_components/SectionTitle';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import ChatbotWidget from './ChatbotWidget';
import { Search } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';



// Styled Components
const StyledCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  transition: all 0.3s ease;
  border: 1px solid #e5e7eb;
  overflow: hidden;

  &:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
    transform: translateY(-5px);
  }
`;

const GradientButton = styled.button`
  background: linear-gradient(135deg, #3b82f6, #60a5fa);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 9999px;
  font-weight: 600;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(59, 130, 246, 0.25);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 8px rgba(59, 130, 246, 0.4);
  }
`;

// Function to Fetch Markdown Files
const importMarkdown = async (filename) => {
  try {
    const response = await fetch(`/markdown/${filename}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${filename}: ${response.statusText}`);
    }
    const text = await response.text();
    return text;
  } catch (error) {
    console.error('Error loading markdown:', error);
    return 'Content could not be loaded.';
  }
};

// Mapping of Markdown Files
const markdownFiles = {
  signup: 'Signup.md',
  login: 'Login.md',
  sidebar: 'Sidebar.md',
  planDataIntro: 'Plan_Data_Intro.md',
  clientListGuide: 'Client_List.md',
  planTouchpointsGuide: 'Plan_Touchpoints.md',
  requirementsChecklistGuide: 'Requirements_Checklist.md',
  planDesignGuide: 'Plan_Design_and_Elections.md',
  planPerformanceGuide: 'Plan_Performance.md',
  advisorServiceGuide: 'Advisor_Service_Schedule.md',
  feeScheduleGuide: 'Fee_Schedule.md',
  // prospectingGuide: 'Prospecting.md',
  // participantsTableGuide: 'Participants_Table.md',
  aiRewriterGuide: 'AI_Rewriter.md',
  emailAutomationGuide: 'Email_Automation.md',
  fileLibraryGuide: 'File_Library.md',
  marketingGuide: 'Marketing.md',
  reportingGuide: 'Reporting.md',
  dashboardGuide: 'Dashboard.md',
  requirementsCalendarGuide: 'Requirements_Calendar.md',
  advisorResourcesGuide: 'Advisor_Resources.md',
  advisorFeeCalculatorGuide: 'Advisor_Fee_Calculator.md',
  // sendingCensusGuide: 'Sending_Participant_Census.md',
  // receivingCensusGuide: 'Receiving_Participant_Census_Response.md',
  chatbotGuide: 'User_Guide_Chatbot.md',
};

const HelpAndResourcePage = () => {
  // State Variables
  const [openParentSection, setOpenParentSection] = useState(null);
  const [openSubSection, setOpenSubSection] = useState(null);
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [markdownContent, setMarkdownContent] = useState({});

  // Load Markdown Files on Component Mount
  useEffect(() => {
    const loadMarkdownFiles = async () => {
      const keys = Object.keys(markdownFiles);
      const fetchedContents = await Promise.all(
        keys.map(key => importMarkdown(markdownFiles[key]))
      );
      const content = keys.reduce((acc, key, index) => {
        acc[key] = fetchedContents[index];
        return acc;
      }, {});
      setMarkdownContent(content);
    };
    loadMarkdownFiles();
  }, []);

  // Memoize userGuideContent
  const userGuideContent = useMemo(() => [
    {
      title: 'Getting Started',
      content: 'Get started with PlanSync AI',
      subsections: [
        {
          title: 'Sign Up Guide',
          content: markdownContent.signup || 'Loading...',
        },
        {
          title: 'Login Guide',
          content: markdownContent.login || 'Loading...',
        },
        
      ],
    },
    {
      title: 'Sidebar Guide',
      subsections: [
        {
          title: 'Sidebar Guide',
          content: markdownContent.sidebar || 'Loading...',
        },
      ],
    },
    {
      title: 'Plan Data',
      subsections: [
        {
          title: 'Plan Data Introduction',
          content: markdownContent.planDataIntro || 'Loading...',
        },
        {
          title: 'Client List Guide',
          content: markdownContent.clientListGuide || 'Loading...',
        },
        {
          title: 'Plan Touchpoints Guide',
          content: markdownContent.planTouchpointsGuide || 'Loading...',
        },
        {
          title: 'Requirements Checklist Guide',
          content: markdownContent.requirementsChecklistGuide || 'Loading...',
        },
        {
          title: 'Plan Design and Elections Guide',
          content: markdownContent.planDesignGuide || 'Loading...',
        },
        {
          title: 'Plan Performance Guide',
          content: markdownContent.planPerformanceGuide || 'Loading...',
        },
        {
          title: 'Advisor Service Schedule Guide',
          content: markdownContent.advisorServiceGuide || 'Loading...',
        },
        {
          title: 'Fee Schedule Guide',
          content: markdownContent.feeScheduleGuide || 'Loading...',
        },
        // {
        //   title: 'Client Prospecting Guide',
        //   content: markdownContent.prospectingGuide || 'Loading...',
        // },
        // {
        //   title: 'Participants Table Guide',
        //   content: markdownContent.participantsTableGuide || 'Loading...',
        // },
      ],
    },
    {
      title: 'Features & Tools',
      content: 'Explore additional features and tools available in PlanSync AI.',
      subsections: [
        {
          title: 'AI Rewriter Guide',
          content: markdownContent.aiRewriterGuide || 'Loading...',
        },
        {
          title: 'Email Automation Guide',
          content: markdownContent.emailAutomationGuide || 'Loading...',
        },
        {
          title: 'File Library Guide',
          content: markdownContent.fileLibraryGuide || 'Loading...',
        },
        {
          title: 'Marketing Guide',
          content: markdownContent.marketingGuide || 'Loading...',
        },
        {
          title: 'Reporting Guide',
          content: markdownContent.reportingGuide || 'Loading...',
        },
        {
          title: 'Dashboard Guide',
          content: markdownContent.dashboardGuide || 'Loading...',
        },
        {
          title: 'Requirements Calendar Guide',
          content: markdownContent.requirementsCalendarGuide || 'Loading...',
        },
        {
          title: 'Advisor Resources Guide',
          content: markdownContent.advisorResourcesGuide || 'Loading...',
        },
        {
          title: 'Advisor Fee Calculator Guide',
          content: markdownContent.advisorFeeCalculatorGuide || 'Loading...',
        },
      ],
    },
    {
      title: 'Participant Census',
      content: 'Learn about managing participant census data.',
      subsections: [
        // {
        //   title: 'Sending Participant Census Guide',
        //   content: markdownContent.sendingCensusGuide || 'Loading...',
        // },
        {
          title: 'Receiving Participant Census Guide',
          content: markdownContent.receivingCensusGuide || 'Loading...',
        },
      ],
    },
    {
      title: 'Help & Support',
      content: 'Get help and support using PlanSync AI.',
      subsections: [
        {
          title: 'User Guide Chatbot Guide',
          content: markdownContent.chatbotGuide || 'Loading...',
        },
      ],
    },
  ], [markdownContent]);

  // Generate Search Suggestions
  const searchSuggestions = useMemo(() => {
    const suggestions = [];
    userGuideContent.forEach(section => {
      suggestions.push({
        title: section.title,
        type: 'section',
      });
      section.subsections.forEach(subsection => {
        suggestions.push({
          title: subsection.title,
          parentTitle: section.title,
          type: 'subsection',
        });
      });
    });
    return suggestions;
  }, [userGuideContent]);

  // Filter Suggestions Based on Search Query
  const filteredSuggestions = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return searchSuggestions
      .filter(item => item.title.toLowerCase().includes(query))
      .slice(0, 6);
  }, [searchQuery, searchSuggestions]);

  // Handle Suggestion Click
  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.title);
    setShowSuggestions(false);

    const sectionIndex = userGuideContent.findIndex(section => 
      suggestion.type === 'section' 
        ? section.title === suggestion.title
        : section.title === suggestion.parentTitle
    );

    if (sectionIndex !== -1) {
      setOpenParentSection(sectionIndex);
      if (suggestion.type === 'subsection') {
        const subIndex = userGuideContent[sectionIndex].subsections
          .findIndex(sub => sub.title === suggestion.title);
        if (subIndex !== -1) {
          toggleSubSection(sectionIndex, subIndex);
        }
      }
    }
  };

  // Filter Content Based on Search Query
  const filteredContent = useMemo(() => {
    if (!searchQuery.trim()) return userGuideContent;

    const searchTerm = searchQuery.toLowerCase();
    return userGuideContent.map(section => {
      const sectionMatches = section.title.toLowerCase().includes(searchTerm) ||
                             (section.content && section.content.toLowerCase().includes(searchTerm));

      const matchingSubsections = section.subsections.filter(subsection =>
        subsection.title.toLowerCase().includes(searchTerm) ||
        (subsection.content && subsection.content.toLowerCase().includes(searchTerm))
      );

      if (sectionMatches || matchingSubsections.length > 0) {
        return {
          ...section,
          subsections: matchingSubsections,
          hasMatchingSubsections: matchingSubsections.length > 0,
        };
      }
      return null;
    }).filter(Boolean);
  }, [userGuideContent, searchQuery]);

  // Automatically Open Sections with Matching Subsections
  useEffect(() => {
    if (searchQuery.trim() && filteredContent.length > 0) {
      filteredContent.forEach((section, index) => {
        if (section.hasMatchingSubsections) {
          setOpenParentSection(index);
        }
      });
    }
  }, [searchQuery, filteredContent]);

  // Toggle Parent Section
  const toggleParentSection = (index) => {
    if (searchQuery && filteredContent[index]?.hasMatchingSubsections) {
      setOpenParentSection(index);
    } else {
      setOpenParentSection(openParentSection === index ? null : index);
    }
  };

  // Toggle Subsection
  const toggleSubSection = (parentIndex, subIndex) => {
    const sectionKey = `${parentIndex}-${subIndex}`;
    setOpenSubSection(openSubSection === sectionKey ? null : sectionKey);
    setOpenParentSection(parentIndex);
  };

  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 10,
      },
    },
  };

  // Handle Keyboard Navigation in Search
  const handleKeyDown = (e) => {
    if (!showSuggestions || filteredSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0) {
          handleSuggestionClick(filteredSuggestions[selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
      default:
        break;
    }
  };

  // Reset Selected Suggestion on Search Query Change
  useEffect(() => {
    setSelectedSuggestionIndex(-1);
  }, [searchQuery]);

  return (
    <div className="min-h-screen p-8">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="max-w-6xl mx-auto space-y-12"
      >
        {/* Page Title */}
        <motion.div variants={itemVariants} className="text-center mb-12">
          <SectionTitle className="text-4xl font-bold text-blue-600 mb-4">
            Help & User Guide
          </SectionTitle>
        </motion.div>

        {/* Open Chatbot Button */}
        <motion.div variants={itemVariants} className="text-center mb-12">
          <GradientButton onClick={() => setIsChatbotOpen(true)} className="inline-flex items-center text-lg">
            <ChatBubbleLeftRightIcon className="h-6 w-6 mr-2" />
            Open AI Chatbot Assistant
          </GradientButton>
        </motion.div>

        {/* User Guide Section */}
        <StyledCard variants={itemVariants}>
          <div className="bg-gradient-to-bl from-sky-400 to-blue-800 p-6 -mt-8 -mx-8 rounded-t-xl mb-6">
            <h2 className="text-3xl font-light text-white flex items-center">
              <DocumentTextIcon className="h-8 w-8 mr-3" />
              User Guide
            </h2>
          </div>
          
          {/* Download Complete User Guide Button - Moved here */}
          <div className="mb-6">
            <GradientButton 
              className="w-full justify-center" 
              onClick={() => window.open('https://docs.google.com/document/d/10U5CCnK93Kw1ywSf9VMD01DIzMSZihvfThahUdXnmmQ/edit?tab=t.0#heading=h.uytso7v48nwe', '_blank')}
            >
              Download Complete User Guide (PDF)
            </GradientButton>
          </div>

          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setShowSuggestions(true)}
                onKeyDown={handleKeyDown}
                placeholder="Search user guide..."
                className="w-full py-3 px-4 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                role="combobox"
                aria-expanded={showSuggestions}
                aria-controls="search-suggestions"
                aria-activedescendant={selectedSuggestionIndex >= 0 ? `suggestion-${selectedSuggestionIndex}` : undefined}
              />
              <Search 
                size={20} 
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500"
              />
              
              {/* Search Suggestions Dropdown */}
              {showSuggestions && filteredSuggestions.length > 0 && (
                <div 
                  id="search-suggestions"
                  className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-auto"
                  role="listbox"
                >
                  {filteredSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      id={`suggestion-${index}`}
                      onClick={() => handleSuggestionClick(suggestion)}
                      onMouseEnter={() => setSelectedSuggestionIndex(index)}
                      className={`w-full text-left px-4 py-2 transition-colors duration-150 ${
                        selectedSuggestionIndex === index 
                          ? 'bg-blue-50 text-blue-700' 
                          : 'hover:bg-blue-50'
                      }`}
                      role="option"
                      aria-selected={selectedSuggestionIndex === index}
                    >
                      <div className="flex items-center">
                        {suggestion.type === 'subsection' && (
                          <div className="text-gray-400 mr-2">└</div>
                        )}
                        <div>
                          <div className={`${
                            selectedSuggestionIndex === index ? 'text-blue-700' : 'text-gray-800'
                          }`}>
                            {suggestion.title}
                          </div>
                          {suggestion.type === 'subsection' && (
                            <div className="text-xs text-gray-500">
                              in {suggestion.parentTitle}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {/* User Guide Content */}
          <div className="space-y-4">
            {filteredContent.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No sections found matching "{searchQuery}"
              </div>
            ) : (
              filteredContent.map((section, index) => (
                <div key={index} className="border-b border-gray-200 py-3">
                  <button
                    onClick={() => toggleParentSection(index)}
                    className="flex justify-between items-center w-full text-left font-semibold text-gray-700 hover:text-blue-600 focus:outline-none transition duration-300"
                  >
                    {section.title}
                    {openParentSection === index ? (
                      <ChevronUpIcon className="h-5 w-5 text-blue-500" />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  <AnimatePresence>
                    {openParentSection === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-2 ml-4"
                      >
                        <p className="text-gray-600 mb-4">{section.content}</p>
                        {section.subsections.map((subsection, subIndex) => (
                          <div key={subIndex} className="border-l-2 border-gray-200 pl-4 py-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSubSection(index, subIndex);
                              }}
                              className="flex justify-between items-center w-full text-left text-gray-700 hover:text-blue-600 focus:outline-none transition duration-300"
                            >
                              {subsection.title}
                              {openSubSection === `${index}-${subIndex}` ? (
                                <ChevronUpIcon className="h-4 w-4 text-blue-500" />
                              ) : (
                                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                              )}
                            </button>
                            <AnimatePresence>
                              {openSubSection === `${index}-${subIndex}` && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.3 }}
                                  className="mt-2 text-gray-600"
                                >
                                  <ReactMarkdown
                                    className="prose prose-blue max-w-none"
                                    remarkPlugins={[remarkGfm]}
                                  >
                                    {subsection.content}
                                  </ReactMarkdown>

                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))
            )}
          </div>
        </StyledCard>

        {/* Demo Videos Section
        <StyledCard variants={itemVariants}>
          <div className="bg-gradient-to-bl from-sky-400 to-blue-800 p-6 -mt-8 -mx-8 rounded-t-xl mb-6">
            <h2 className="text-3xl font-light text-white flex items-center">
              <VideoCameraIcon className="h-8 w-8 mr-3" />
              Demo Videos
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="relative rounded-lg overflow-hidden shadow-lg" style={{ paddingTop: '56.25%' }}>
              <iframe 
                className="absolute top-0 left-0 w-full h-full" 
                src="https://www.youtube.com/embed/your-demo-video-1" 
                title="Demo Video 1" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen
              ></iframe>
            </div>
            <div className="relative rounded-lg overflow-hidden shadow-lg" style={{ paddingTop: '56.25%' }}>
              <iframe 
                className="absolute top-0 left-0 w-full h-full" 
                src="https://www.youtube.com/embed/your-demo-video-2" 
                title="Demo Video 2" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </StyledCard> */}

        {/* Contact Information Section */}
        <StyledCard variants={itemVariants}>
          <div className="bg-gradient-to-bl from-sky-400 to-blue-800 p-6 -mt-8 -mx-8 rounded-t-xl mb-6">
            <h2 className="text-3xl font-light text-white flex items-center">
              <PhoneIcon className="h-8 w-8 mr-3" />
              Contact Information
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col items-center p-4 bg-blue-50 rounded-lg">
              <EnvelopeIcon className="h-10 w-10 text-blue-500 mb-3" />
              <a href="mailto:info@401k-pro.ai" className="text-blue-600 hover:underline text-lg">
                info@401k-pro.ai
              </a>
            </div>
            <div className="flex flex-col items-center p-4 bg-blue-50 rounded-lg">
              <PhoneIcon className="h-10 w-10 text-blue-500 mb-3" />
              <a href="tel:+19192009943" className="text-blue-600 hover:underline text-lg">
                +1 (919) 200-9943
              </a>
            </div>
          </div>
        </StyledCard>

        {/* Additional Resources Section */}
        <StyledCard variants={itemVariants}>
          <div className="bg-gradient-to-bl from-sky-400 to-blue-800 p-6 -mt-8 -mx-8 rounded-t-xl mb-6">
            <h2 className="text-3xl font-light text-white flex items-center">
              <GlobeAltIcon className="h-8 w-8 mr-3" />
              Additional Resources
            </h2>
          </div>
          <ul className="space-y-4 text-gray-700">
            <li>
              <a href="/faqs" className="text-blue-600 hover:underline text-lg flex items-center">
                <DocumentTextIcon className="h-6 w-6 mr-2" />
                Frequently Asked Questions (FAQs)
              </a>
            </li>
            <li>
              <a href="/blog" className="text-blue-600 hover:underline text-lg flex items-center">
                <DocumentTextIcon className="h-6 w-6 mr-2" />
                PlanSync AI Blog
              </a>
            </li>
            <li>
              <a href="/webinars" className="text-blue-600 hover:underline text-lg flex items-center">
                <VideoCameraIcon className="h-6 w-6 mr-2" />
                Upcoming Webinars
              </a>
            </li>
          </ul>
        </StyledCard>
      </motion.div>

      {/* Chatbot Widget */}
      <ChatbotWidget isOpen={isChatbotOpen} onClose={() => setIsChatbotOpen(false)} />
    </div>
  );
};


export default HelpAndResourcePage;
